/**
 * Shared list views (`listViews.*`): table / cards / board switcher, filters, grouping, saved views.
 * Merged through `extra.ts`; `listViewsEn` is typed against `listViewsPl`, so a missing key is a compile error.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const listViewsPl = {
  viewLabel: 'Widok listy',
  table: 'Lista',
  cards: 'Kafelki',
  board: 'Tablica',
  calendar: 'Kalendarz',
  dragHint: 'Przeciągnij na inną kolumnę, aby zmienić status',
  bulk: {
    title: 'Operacje zbiorcze',
    selected: 'Zaznaczono: {count}',
    selectAll: 'Zaznacz wszystkie ({count})',
    deselectAll: 'Odznacz wszystkie',
    csv: 'CSV zaznaczonych',
    clear: 'Wyczyść zaznaczenie',
    tick: 'Zaznacz pozycję',
    tickAll: 'Zaznacz wszystkie na stronie',
  },
  cal: {
    prev: 'Poprzedni miesiąc',
    next: 'Następny miesiąc',
    today: 'Dziś',
    more: '+{count} więcej',
    less: 'Zwiń',
    undated: 'Bez daty ({count})',
  },
  fields: { status: 'Status', route: 'Trasa', owner: 'Opiekun', city: 'Miasto', day: 'Dzień', customer: 'Klient', document: 'Dokument', late: 'Spóźnienie (dni)' },
  searchPlaceholder: 'Szukaj na liście…',
  filters: 'Filtry',
  filtersActive: 'Filtry ({count})',
  clearFilters: 'Wyczyść filtry',
  noValue: 'Brak wartości',
  groupBy: 'Grupuj wg',
  groupNone: 'Bez grupowania',
  boardBy: 'Kolumny wg',
  groupCount: '{count} poz.',
  expandAll: 'Rozwiń wszystkie',
  collapseAll: 'Zwiń wszystkie',
  density: 'Gęstość',
  densityComfortable: 'Standardowa',
  densityCompact: 'Zwarta',
  exportCsv: 'Eksport widoku do CSV',
  copyLink: 'Kopiuj link do tego widoku',
  linkCopied: 'Skopiowano link',
  shortcuts: 'Skróty: / szukaj · 1 2 3 tryb · g grupowanie',
  pageScope: 'Filtry i grupy dotyczą załadowanej strony: {shown} z {total}. Zwiększ rozmiar strony, aby objąć więcej.',
  filteredOut: 'Filtry ukrywają {hidden} z {shown} pozycji na tej stronie.',
  emptyFiltered: 'Żadna pozycja nie spełnia wybranych filtrów.',
  boardNeedsField: 'Tablica wymaga kolumny ze statusem.',
  saved: {
    title: 'Zapisane widoki',
    save: 'Zapisz bieżący widok…',
    namePlaceholder: 'Nazwa widoku',
    saveAction: 'Zapisz',
    apply: 'Zastosuj widok: {name}',
    remove: 'Usuń widok: {name}',
    empty: 'Brak zapisanych widoków. Ustaw filtry, grupowanie i tryb, a potem zapisz.',
    reset: 'Przywróć domyślny widok',
    saved: 'Zapisano widok',
    error: 'Nie udało się zapisać widoku.',
    shared: 'Udostępniony zespołowi',
    fromTeam: 'Od zespołu',
    share: 'Udostępnij zespołowi',
    unshare: 'Przestań udostępniać',
    shareWithTeam: 'Udostępnij zespołowi (tylko do odczytu)',
    alertMe: 'Powiadamiaj mnie o nowych pozycjach',
    alertOn: 'Powiadamiaj o nowych pozycjach',
    alertOff: 'Wyłącz powiadomienia',
  },
};

export const listViewsEn: Shape<typeof listViewsPl> = {
  viewLabel: 'List view',
  table: 'List',
  cards: 'Cards',
  board: 'Board',
  calendar: 'Calendar',
  dragHint: 'Drag to another column to change the status',
  bulk: {
    title: 'Bulk actions',
    selected: 'Selected: {count}',
    selectAll: 'Select all ({count})',
    deselectAll: 'Deselect all',
    csv: 'CSV of selection',
    clear: 'Clear selection',
    tick: 'Select item',
    tickAll: 'Select all on the page',
  },
  cal: {
    prev: 'Previous month',
    next: 'Next month',
    today: 'Today',
    more: '+{count} more',
    less: 'Collapse',
    undated: 'No date ({count})',
  },
  fields: { status: 'Status', route: 'Route', owner: 'Owner', city: 'City', day: 'Day', customer: 'Customer', document: 'Document', late: 'Delay (days)' },
  searchPlaceholder: 'Search this list…',
  filters: 'Filters',
  filtersActive: 'Filters ({count})',
  clearFilters: 'Clear filters',
  noValue: 'No value',
  groupBy: 'Group by',
  groupNone: 'No grouping',
  boardBy: 'Columns by',
  groupCount: '{count} items',
  expandAll: 'Expand all',
  collapseAll: 'Collapse all',
  density: 'Density',
  densityComfortable: 'Comfortable',
  densityCompact: 'Compact',
  exportCsv: 'Export view to CSV',
  copyLink: 'Copy link to this view',
  linkCopied: 'Link copied',
  shortcuts: 'Shortcuts: / search · 1 2 3 mode · g grouping',
  pageScope: 'Filters and groups apply to the loaded page: {shown} of {total}. Raise the page size to cover more.',
  filteredOut: 'Filters hide {hidden} of {shown} items on this page.',
  emptyFiltered: 'No item matches the selected filters.',
  boardNeedsField: 'The board needs a status column.',
  saved: {
    title: 'Saved views',
    save: 'Save current view…',
    namePlaceholder: 'View name',
    saveAction: 'Save',
    apply: 'Apply view: {name}',
    remove: 'Delete view: {name}',
    empty: 'No saved views yet. Set filters, grouping and mode, then save.',
    reset: 'Reset to the default view',
    saved: 'View saved',
    error: 'Could not save the view.',
    shared: 'Shared with the team',
    fromTeam: 'From the team',
    share: 'Share with the team',
    unshare: 'Stop sharing',
    shareWithTeam: 'Share with the team (read-only)',
    alertMe: 'Notify me about new items',
    alertOn: 'Notify about new items',
    alertOff: 'Turn notifications off',
  },
};
